/**
 * L'identité installable de la page (PWA) selon la façade.
 *
 * `public/index.html` est commun aux deux produits : il pointe `manifest.json`
 * (Anagogia). Sur un hôte d'Azaria, « Ajouter à l'écran d'accueil » doit
 * installer Azaria — nom, icône, couleur —, jamais Anagogia. Ce module repose,
 * au démarrage et avant le premier rendu, le manifeste et les balises lues par
 * les téléphones (Android lit le manifeste, iOS l'icône `apple-touch-icon`, le
 * titre `apple-mobile-web-app-title` et `<title>` au moment de l'ajout).
 *
 * Le nom du manifeste d'Azaria est figé dans `public/manifest-azaria.json` ; le
 * nom affiché ailleurs suit `REACT_APP_FACADE_NAME` (`NOM_DU_PRODUIT`).
 */
import type { Facade } from './resolveFacade';

export interface IdentiteDeLaPage {
    manifeste: string;
    icone: string;
    couleur: string;
    titre: string;
}

export const MANIFESTE_AZARIA = '/manifest-azaria.json';
export const ICONE_AZARIA = '/icons/azaria-192.png';
export const COULEUR_AZARIA = '#1F2A44';

/** L'identité à poser, ou `null` sous Anagogia (la page reste telle que servie). */
export function identiteDeLaPage(facade: Facade, nom: string): IdentiteDeLaPage | null {
    if (facade !== 'azaria') return null;
    return { manifeste: MANIFESTE_AZARIA, icone: ICONE_AZARIA, couleur: COULEUR_AZARIA, titre: nom };
}

function poserLien(doc: Document, rel: string, href: string): void {
    let lien = doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!lien) {
        lien = doc.createElement('link');
        lien.rel = rel;
        doc.head.appendChild(lien);
    }
    lien.href = href;
}

function poserMeta(doc: Document, name: string, content: string): void {
    let meta = doc.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!meta) {
        meta = doc.createElement('meta');
        meta.name = name;
        doc.head.appendChild(meta);
    }
    meta.content = content;
}

/** Pose l'identité de la façade dans le document ; rien sous Anagogia. */
export function appliquerIdentite(doc: Document, facade: Facade, nom: string): void {
    const identite = identiteDeLaPage(facade, nom);
    if (!identite) return;
    poserLien(doc, 'manifest', identite.manifeste);
    poserLien(doc, 'apple-touch-icon', identite.icone);
    poserMeta(doc, 'theme-color', identite.couleur);
    poserMeta(doc, 'application-name', identite.titre);
    poserMeta(doc, 'apple-mobile-web-app-title', identite.titre);
    doc.title = identite.titre;
}
